/**
 * MemberProfileView — World Cup profile opened from a leaderboard row.
 * Peers: read-only futures, stats, and finished/live match cards.
 * Self: the full editable Picks feed (futures + editable match cards + the
 * upcoming/finished toggle) with a stats + rank header bolted on top.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, ScrollView, Pressable, Platform, ActivityIndicator, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { ChevronLeft, Target, Zap, Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useWorldCupMemberProfile } from '../../../src/worldcup/hooks/useWorldCupMemberProfile';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { useCompetitionId, useCompetitionBasePath, useCompetitionSurface } from '../../../src/competitions/contexts/CompetitionContext';
import { resolveMemberProfileBody } from '../../../src/competitions/helpers/memberProfileBody.helper';
import { profileMatchToWorldCupMatch } from '../../../src/worldcup/helpers/worldCupMemberProfile.helper';
import {
  resolveCompetitionBackRoute,
  competitionMatchRoute,
  competitionMatchStatsRoute,
} from '../../../src/competitions/helpers/competitionRoutes.helper';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { isAvatarUrl } from '../../../src/create/helpers/createUi.helper';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { MatchCard } from '../MatchesView/MatchCard';
import { PicksView } from '../PicksView/PicksView';
import { MemberFuturesDisplay } from './MemberFuturesDisplay';
import { getMemberProfileViewStyles } from './MemberProfileView.styles';
import { MEMBER_PROFILE_COPY, MEMBER_PROFILE_TEST_IDS } from './MemberProfileView.constants';
import type { WorldCupMatch } from '../../../src/worldcup/types/worldcup.types';

interface MemberProfileViewProps {
  groupId: string;
  memberId: string;
  /** The WC tab the viewer came from — used to land back there on refresh/deep link. */
  wcTab?: string;
  /** True when the viewer opened their own profile — renders the editable Picks feed. */
  isSelf?: boolean;
  /** The viewer's leaderboard rank, passed from the row they tapped (self mode only). */
  rank?: number | null;
}


export const MemberProfileView: React.FC<MemberProfileViewProps> = ({ groupId, memberId, wcTab, isSelf = false, rank = null }) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  // Flags for national teams, kits for clubs (AC-3.8) — the same surface test the leaderboard
  // uses for AC-3.6. Defaulting off the surface rather than off "did the server send a kit?"
  // keeps the World Cup unmoved whatever colours its teams happen to carry in the database.
  const surface = useCompetitionSurface();
  const teamVisual = surface === 'worldcup' ? 'flag' : 'kit';
  const { isSignedIn, isLoaded, userId } = useSupabaseAuth();
  const { width: windowWidth } = useWindowDimensions();

  // Which body to render, and whether this really is the viewer's own profile. The editable
  // World Cup feed is competition-blind, so it is only ever offered on the World Cup — see the
  // helper for why (specs/competitions-hub-client Story 19).
  const { isOwnProfile, body } = resolveMemberProfileBody({
    isSelfParam: isSelf,
    viewerUserId: userId,
    memberId,
    competitionId,
  });
  const styles = useMemo(
    () => getMemberProfileViewStyles(theme, themeMode, windowWidth),
    [theme, themeMode, windowWidth],
  );

  const { data, isLoading, isError } = useWorldCupMemberProfile(
    competitionId,
    groupId,
    memberId,
    { enabled: isLoaded && isSignedIn },
  );

  const matchesQuery = useWorldCupMatchesQuery(competitionId, isLoaded && isSignedIn);
  const futuresOptions = matchesQuery.data?.futuresOptions ?? null;

  const matches = useMemo<WorldCupMatch[]>(
    () => (data?.matches ?? []).map(profileMatchToWorldCupMatch),
    [data?.matches],
  );

  const handleBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      // Must resolve against THIS competition's base path: the generic shell renders
      // this same component, and a WC-bound fallback ejects the viewer out of their
      // competition into the World Cup stack holding a foreign group id.
      router.replace(resolveCompetitionBackRoute(basePath, groupId, wcTab) as never);
    }
  }, [router, basePath, groupId, wcTab]);

  // Both card actions resolve against THIS competition's base path (AC-10.10). The stats
  // route used to be World-Cup-only, so the button was deliberately inert on a generic
  // competition; the fixture button was never wired at all, which made it dead on every
  // surface, the World Cup included.
  const handleStatsPress = useCallback(
    (matchId: string) => {
      router.push(competitionMatchStatsRoute(basePath, matchId) as never);
    },
    [router, basePath],
  );

  const handleFixturePress = useCallback(
    (matchId: string) => {
      router.push(competitionMatchRoute(basePath, matchId) as never);
    },
    [router, basePath],
  );

  // Profile header: avatar, name, optional rank badge, and the stat chips. Shared by both
  // bodies — the rank badge only has a value for the viewer's own row, so peers render exactly
  // what they always did. Gated on `data` so it pops in when the profile fetch resolves without
  // blocking the (separately loaded) picks feed.
  // One number, and the split that explains it (specs/champions-league AC-18.4).
  //
  // The squad leaderboard ranks members by settled + live and shows that total; this screen
  // used to show `stats.totalPoints`, the settled standing alone — so tapping a member showing
  // 145 opened a profile saying 140. Both fields are optional, so a server that predates them
  // falls back to exactly today's number rather than to zero.
  const settledPoints = data?.stats.totalPoints ?? 0;
  const livePoints = data?.stats.livePoints ?? 0;
  const displayPoints = data?.stats.provisionalPoints ?? settledPoints;
  const showPointsBreakdown = livePoints > 0;

  const profileHeaderBlock = data ? (
    <>
      <View style={styles.profileHeader}>
        <FriendAvatar
          avatarId={data.user.avatarId ? normalizeDisplayAvatarId(data.user.avatarId) : undefined}
          avatarConfig={data.user.avatarConfig}
          fallbackUrl={
            data.user.avatar && isAvatarUrl(data.user.avatar.url) ? data.user.avatar.url : undefined
          }
          size={72}
          nameForInitials={data.user.name}
          avatarRounded
        />
        <View style={styles.selfNameRow}>
          <Text style={styles.memberName}>{data.user.name}</Text>
          {typeof rank === 'number' && rank > 0 ? (
            <View style={styles.rankBadge}>
              <Text style={styles.rankBadgeText}>{`#${rank}`}</Text>
            </View>
          ) : null}
        </View>
      </View>
      <View style={styles.statsRow}>
        <View style={styles.statChip}>
          <Trophy size={14} color={theme.primary} />
          <Text style={styles.statValue} testID={MEMBER_PROFILE_TEST_IDS.points}>
            {displayPoints}
          </Text>
          <Text style={styles.statLabel}>{MEMBER_PROFILE_COPY.STAT_LABEL_PTS}</Text>
        </View>
        <View style={styles.statChip}>
          <Target size={14} color={theme.success ?? theme.primary} />
          <Text style={styles.statValue}>{data.stats.exactHitCount}</Text>
          <Text style={styles.statLabel}>{MEMBER_PROFILE_COPY.STAT_LABEL_EXACT}</Text>
        </View>
        <View style={styles.statChip}>
          <Zap size={14} color={theme.warning ?? theme.primary} />
          <Text style={styles.statValue}>{data.stats.correctResultCount}</Text>
          <Text style={styles.statLabel}>{MEMBER_PROFILE_COPY.STAT_LABEL_CORRECT}</Text>
        </View>
      </View>
      {showPointsBreakdown ? (
        <Text
          style={styles.pointsBreakdown}
          testID={MEMBER_PROFILE_TEST_IDS.pointsBreakdown}
        >
          {MEMBER_PROFILE_COPY.pointsBreakdown(settledPoints, livePoints)}
        </Text>
      ) : null}
    </>
  ) : null;

  return (
    <View style={styles.container}>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.backButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.backButtonPressed,
          hovered && Platform.OS === 'web' && styles.backButtonHover,
        ]}
        onPress={handleBack}
        accessibilityRole="button"
        accessibilityLabel="Go back"
      >
        <ChevronLeft size={20} color={theme.primary} />
        <Text style={styles.backLabel}>{MEMBER_PROFILE_COPY.BACK_LABEL}</Text>
      </Pressable>

      {body === 'worldcup-editable-feed' ? (
        // World Cup self: the editable Picks feed handles futures + match cards + the
        // upcoming/finished toggle; the stats/rank header rides on top.
        <PicksView headerSlot={profileHeaderBlock} />
      ) : isLoading ? (
        <View style={styles.loadingState}>
          <ActivityIndicator color={theme.primary} />
        </View>
      ) : isError || !data ? (
        <View style={styles.errorState}>
          <Text style={styles.errorText}>{MEMBER_PROFILE_COPY.LOAD_ERROR}</Text>
        </View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
        >
          {profileHeaderBlock}

          {isOwnProfile && (
            <Text style={styles.selfReadOnlyHint}>{MEMBER_PROFILE_COPY.SELF_READ_ONLY_HINT}</Text>
          )}

          {/* Futures picks — read-only here, including on your own profile outside the World
              Cup. The server sends a viewer their own futures unhidden, so this shows YOUR
              picks; editing them lives on the competition's Picks tab. */}
          <Text style={styles.sectionTitle}>{MEMBER_PROFILE_COPY.SECTION_FUTURES}</Text>
          <MemberFuturesDisplay
            futures={data.futures}
            futuresHidden={data.futuresHidden ?? false}
            futuresOptions={futuresOptions}
            teamVisual={teamVisual}
            styles={styles}
            theme={theme}
          />

          {/* Match cards */}
          {matches.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>{MEMBER_PROFILE_COPY.SECTION_MATCHES}</Text>
              {matches.map((match) => (
                <View key={match.id} style={styles.matchCardWrapper}>
                  <MatchCard
                    match={match}
                    isBoosted={match.prediction?.isBoosted ?? false}
                    predictionLabel={data.user.name}
                    onStatsPress={handleStatsPress}
                    onFixturePress={handleFixturePress}
                    teamVisual={teamVisual}
                  />
                </View>
              ))}
            </>
          )}

          {matches.length === 0 && (
            <View style={styles.emptyMatches}>
              <Text style={styles.emptyMatchesText}>{MEMBER_PROFILE_COPY.EMPTY_MATCHES}</Text>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
};
